import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: string
  structuredData?: object
}

// SEO configuration for different routes
const seoConfig: Record<string, {
  en: { title: string; description: string; keywords: string }
  es: { title: string; description: string; keywords: string }
}> = {
  '/': {
    en: {
      title: "LogiCloud Mexico - Cloud, DevOps, AI & Machine Learning Services | Top Tech Solutions in Mexico",
      description: "LogiCloud Mexico - Leading provider of cloud, DevOps, AI and machine learning services for startups and businesses. Specializing in cloud infrastructure, DevOps automation, artificial intelligence, machine learning, route planner, driving directions, map navigation, and innovative mobile applications in Mexico. Expert cloud services, AI solutions, and tech consulting.",
      keywords: "cloud services Mexico, DevOps services Mexico, AI solutions Mexico, machine learning Mexico, cloud infrastructure, DevOps automation, artificial intelligence, machine learning, startup services, LogiCloud, Mexico tech services, AI development, cloud computing, ML services, route planner, free route planner, driving directions, map navigation, get directions, turn-by-turn directions, route planning, navigation app, GPS directions, map directions, worldwide route planner, international directions, global navigation, Mexico City tech company, cloud consulting Mexico, AI consulting Mexico, DevOps consulting Mexico, software development Mexico"
    },
    es: {
      title: "LogiCloud México - Servicios de Cloud, DevOps, IA y Machine Learning | Soluciones Tech en México",
      description: "LogiCloud México - Proveedor líder de servicios de cloud, DevOps, IA y machine learning para startups y empresas. Especializándonos en infraestructura en la nube, automatización DevOps, inteligencia artificial, machine learning, planificador de rutas, indicaciones de conducción, navegación por mapas y aplicaciones móviles innovadoras en México. Expertos en servicios cloud, soluciones IA y consultoría tecnológica.",
      keywords: "servicios cloud México, servicios DevOps México, soluciones IA México, machine learning México, infraestructura cloud, automatización DevOps, inteligencia artificial, machine learning, servicios startups, LogiCloud, servicios tech México, desarrollo IA, computación cloud, servicios ML, planificador de rutas, planificador de rutas gratis, indicaciones de conducción, navegación por mapas, obtener direcciones, direcciones paso a paso, planificación de rutas, aplicación de navegación, direcciones GPS, direcciones de mapas, planificador de rutas mundial, direcciones internacionales, navegación global, empresa tech Ciudad de México, consultoría cloud México, consultoría IA México, consultoría DevOps México, desarrollo de software México"
    }
  },
  '/book-demo': {
    en: {
      title: "Book Demo - LogiCloud Mexico | Schedule a Demo Session",
      description: "Schedule a demo session with our team to explore AI and cloud solutions. Book your appointment today to see how LogiCloud can help your business grow with innovative technology solutions.",
      keywords: "book demo, schedule demo, LogiCloud demo, AI demo, cloud services demo, DevOps demo, machine learning demo, tech consultation, Mexico tech services, business demo"
    },
    es: {
      title: "Reservar Demo - LogiCloud México | Agenda una Sesión de Demo",
      description: "Agenda una sesión de demo con nuestro equipo para explorar soluciones de IA y cloud. Reserva tu cita hoy para ver cómo LogiCloud puede ayudar a tu negocio a crecer con soluciones tecnológicas innovadoras.",
      keywords: "reservar demo, agendar demo, demo LogiCloud, demo IA, demo servicios cloud, demo DevOps, demo machine learning, consultoría tech, servicios tech México, demo empresarial"
    }
  },
  '/about': {
    en: {
      title: "About Us - LogiCloud Mexico | Our Team & Values",
      description: "We are a small team that thrives to grow with your business. Learn about our values, mission, and how we help startups and businesses succeed with innovative technology solutions in Mexico.",
      keywords: "about LogiCloud, LogiCloud team, Mexico tech company, cloud services team, DevOps team, AI team, tech company Mexico, startup services, business growth, technology solutions"
    },
    es: {
      title: "Nosotros - LogiCloud México | Nuestro Equipo y Valores",
      description: "Somos un equipo pequeño que prospera creciendo con tu negocio. Conoce nuestros valores, misión y cómo ayudamos a startups y empresas a tener éxito con soluciones tecnológicas innovadoras en México.",
      keywords: "sobre LogiCloud, equipo LogiCloud, empresa tech México, equipo servicios cloud, equipo DevOps, equipo IA, empresa tecnología México, servicios startups, crecimiento empresarial, soluciones tecnológicas"
    }
  },
  '/services': {
    en: {
      title: "Services - LogiCloud Mexico | Cloud, DevOps, AI & ML Services",
      description: "Discover our comprehensive technology services including cloud infrastructure, DevOps automation, AI solutions, machine learning, and mobile applications. Expert tech services for startups and businesses in Mexico.",
      keywords: "cloud services, DevOps services, AI services, machine learning services, cloud infrastructure, DevOps automation, AI solutions, ML services, mobile applications, tech services Mexico"
    },
    es: {
      title: "Servicios - LogiCloud México | Servicios de Cloud, DevOps, IA y ML",
      description: "Descubre nuestros servicios tecnológicos completos que incluyen infraestructura cloud, automatización DevOps, soluciones IA, machine learning y aplicaciones móviles. Servicios tech expertos para startups y empresas en México.",
      keywords: "servicios cloud, servicios DevOps, servicios IA, servicios machine learning, infraestructura cloud, automatización DevOps, soluciones IA, servicios ML, aplicaciones móviles, servicios tech México"
    }
  },
  '/contact': {
    en: {
      title: "Contact Us - LogiCloud Mexico | Get in Touch",
      description: "Get in touch with LogiCloud Mexico. Contact us for cloud services, DevOps solutions, AI consulting, and machine learning services. We're here to help your business grow.",
      keywords: "contact LogiCloud, LogiCloud Mexico contact, tech consultation, cloud services contact, DevOps consultation, AI consultation, business consultation, Mexico tech services"
    },
    es: {
      title: "Contáctanos - LogiCloud México | Ponte en Contacto",
      description: "Ponte en contacto con LogiCloud México. Contáctanos para servicios cloud, soluciones DevOps, consultoría IA y servicios de machine learning. Estamos aquí para ayudar a tu negocio a crecer.",
      keywords: "contactar LogiCloud, contacto LogiCloud México, consultoría tech, contacto servicios cloud, consultoría DevOps, consultoría IA, consultoría empresarial, servicios tech México"
    }
  },
  '/features': {
    en: {
      title: "Features - LogiCloud Mexico | How We Help Your Business",
      description: "Learn how LogiCloud helps your business with clear communication, customer satisfaction, and better teamwork. Discover our features and tools designed to help you work better with your customers.",
      keywords: "LogiCloud features, business features, customer communication, project management, team collaboration, business tools, customer satisfaction, transparent communication"
    },
    es: {
      title: "Características - LogiCloud México | Cómo Ayudamos a Tu Negocio",
      description: "Aprende cómo LogiCloud ayuda a tu negocio con comunicación clara, satisfacción del cliente y mejor trabajo en equipo. Descubre nuestras características y herramientas diseñadas para ayudarte a trabajar mejor con tus clientes.",
      keywords: "características LogiCloud, características empresariales, comunicación con clientes, gestión de proyectos, colaboración en equipo, herramientas empresariales, satisfacción del cliente, comunicación transparente"
    }
  },
  '/events-seminars': {
    en: {
      title: "Events & Seminars - LogiCloud Mexico | Tech Events, Competitive Programming, Sponsorships",
      description: "Discover LogiCloud's latest tech events, seminars, and sponsorships in Mexico. Join competitive programming competitions, technology seminars, and industry networking events. Stay updated with our community engagement initiatives and educational programs at Tecnológico de Monterrey and other leading institutions.",
      keywords: "tech events Mexico, programming competitions Mexico, competitive programming events, technology seminars Mexico, tech sponsorships, LogiCloud events, programming contests, algorithm competitions, tech community events, Mexico tech meetups, university programming competitions, Tecnológico de Monterrey events, CCM campus events, ITC programming, technology education events, computer science competitions, software development seminars, AI events Mexico, cloud computing seminars, DevOps events Mexico, technology workshops, coding competitions, programming challenges, tech networking events, industry seminars, educational tech events, student programming contests, technology conferences Mexico, startup events, tech community engagement"
    },
    es: {
      title: "Eventos y Seminarios - LogiCloud México | Eventos Tech, Programación Competitiva, Patrocinios",
      description: "Descubre los últimos eventos tecnológicos, seminarios y patrocinios de LogiCloud en México. Únete a competencias de programación competitiva, seminarios tecnológicos y eventos de networking de la industria. Mantente actualizado con nuestras iniciativas de participación comunitaria y programas educativos en el Tecnológico de Monterrey y otras instituciones líderes.",
      keywords: "eventos tech México, competencias de programación México, eventos programación competitiva, seminarios tecnológicos México, patrocinios tech, eventos LogiCloud, concursos de programación, competencias de algoritmos, eventos comunidad tech, meetups tech México, competencias programación universitaria, eventos Tecnológico de Monterrey, eventos campus CCM, programación ITC, eventos educación tecnológica, competencias ciencias de la computación, seminarios desarrollo de software, eventos IA México, seminarios computación en la nube, eventos DevOps México, talleres tecnológicos, competencias de codificación, desafíos de programación, eventos networking tech, seminarios industriales, eventos educativos tech, concursos programación estudiantes, conferencias tecnología México, eventos startups, participación comunidad tech"
    }
  }
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = "https://logicloud.mx/logo.png",
  url,
  type = "website",
  structuredData
}) => {
  const { language } = useLanguage()
  const location = useLocation()

  // Get route-specific SEO content or use defaults
  const routeConfig = useMemo(() => {
    const pathname = location.pathname
    return seoConfig[pathname] || seoConfig['/']
  }, [location.pathname])

  // Determine the full URL
  const fullUrl = useMemo(() => {
    if (url) return url
    const baseUrl = "https://logicloud.mx"
    const path = location.pathname === '/' ? '' : location.pathname
    return `${baseUrl}${path}`
  }, [url, location.pathname])

  // Dynamic SEO content based on language and route
  const seoContent = useMemo(() => {
    const routeContent = routeConfig[language as keyof typeof routeConfig] || routeConfig.en
    return {
      title: title || routeContent.title,
      description: description || routeContent.description,
      keywords: keywords || routeContent.keywords,
      siteName: language === 'es' ? "LogiCloud México" : "LogiCloud Mexico"
    }
  }, [title, description, keywords, routeConfig, language])

  const currentContent = seoContent
  
  // Helper function to get default structured data
  const getDefaultStructuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://logicloud.mx/#organization",
        "name": currentContent.siteName,
        "alternateName": ["logicloud.mx", "LogiCloud", "LogiCloud Limited"],
        "url": "https://logicloud.mx/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://logicloud.mx/logo.png",
          "width": 200,
          "height": 200
        },
        "image": "https://logicloud.mx/logo.png",
        "description": currentContent.description,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Mexico City",
          "addressLocality": "Mexico City",
          "addressRegion": "CDMX",
          "postalCode": "00000",
          "addressCountry": "MX"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "19.4326",
          "longitude": "-99.1332"
        },
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "Coming soon!",
            "contactType": "customer service",
            "email": "info@logicloud.mx",
            "areaServed": "MX",
            "availableLanguage": ["en", "es"]
          },
          {
            "@type": "ContactPoint",
            "telephone": "Coming soon!",
            "contactType": "sales",
            "email": "info@logicloud.mx",
            "areaServed": "MX",
            "availableLanguage": ["en", "es"]
          }
        ],
        "sameAs": [
          "https://www.linkedin.com/company/logicloud-limited",
          "https://noozter.com/",
          "https://noozter.com/track-map",
          "https://programmingalternatives.com/"
        ],
        "foundingDate": "2020",
        "numberOfEmployees": {
          "@type": "QuantitativeValue",
          "value": "10-50"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Mexico"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://logicloud.mx/#website",
        "url": "https://logicloud.mx/",
        "name": currentContent.siteName,
        "description": currentContent.description,
        "publisher": {
          "@id": "https://logicloud.mx/#organization"
        },
        "inLanguage": language === 'es' ? 'es-MX' : 'en-US',
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://logicloud.mx/?s={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebPage",
        "@id": fullUrl,
        "url": fullUrl,
        "name": currentContent.title,
        "isPartOf": {
          "@id": "https://logicloud.mx/#website"
        },
        "description": currentContent.description,
        "inLanguage": language === 'es' ? 'es-MX' : 'en-US',
        "breadcrumb": {
          "@id": "https://logicloud.mx/#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://logicloud.mx/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://logicloud.mx/"
          }
        ]
      },
      {
        "@type": "Service",
        "serviceType": "Cloud Services",
        "provider": {
          "@id": "https://logicloud.mx/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Mexico"
        },
        "description": "Professional cloud infrastructure services including cloud migration, infrastructure management, and cloud consulting in Mexico."
      },
      {
        "@type": "Service",
        "serviceType": "DevOps Services",
        "provider": {
          "@id": "https://logicloud.mx/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Mexico"
        },
        "description": "DevOps automation and CI/CD pipeline services for startups and businesses in Mexico."
      },
      {
        "@type": "Service",
        "serviceType": "AI & Machine Learning Services",
        "provider": {
          "@id": "https://logicloud.mx/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Mexico"
        },
        "description": "Artificial intelligence and machine learning solutions including AI consulting, ML model development, and AI integration services."
      },
      {
        "@type": "SoftwareApplication",
        "name": "Noozter",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web, iOS, Android",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "reviewCount": "50"
        },
        "description": "Collaborative business communication platform for teams and customers."
      },
      {
        "@type": "SoftwareApplication",
        "name": "Track Map - Route Planner",
        "applicationCategory": "NavigationApplication",
        "operatingSystem": "Web, iOS, Android",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "100"
        },
        "description": "Free route planner with driving directions and map navigation."
      },
      {
        "@type": "LocalBusiness",
        "@id": "https://logicloud.mx/#localbusiness",
        "name": currentContent.siteName,
        "image": "https://logicloud.mx/logo.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Mexico City",
          "addressLocality": "Mexico City",
          "addressRegion": "CDMX",
          "postalCode": "00000",
          "addressCountry": "MX"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "19.4326",
          "longitude": "-99.1332"
        },
        "url": "https://logicloud.mx/",
        "telephone": "Coming soon!",
        "priceRange": "$$",
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday"
          ],
          "opens": "09:00",
          "closes": "18:00"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Best"
        },
        "datePublished": "2024-01-15",
        "reviewBody": "Logicloud mobile app will save you thousands of hours of work and has unlocked insights we never thought possible.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "itemReviewed": {
          "@type": "Organization",
          "@id": "https://logicloud.mx/#organization"
        }
      }
    ]
  }), [currentContent, fullUrl, language])

  // Merge custom structured data with default structured data
  const mergedStructuredData = useMemo(() => {
    const defaultData = getDefaultStructuredData
    if (structuredData) {
      const customData = structuredData as any
      // If custom structured data is provided, merge it intelligently
      if (customData["@graph"] && Array.isArray(customData["@graph"])) {
        // Custom data has @graph, merge arrays
        return {
          ...customData,
          "@graph": [...defaultData["@graph"], ...customData["@graph"]]
        }
      } else if (customData["@type"]) {
        // Single schema object, wrap it
        return {
          "@context": "https://schema.org",
          "@graph": [...defaultData["@graph"], customData]
        }
      } else {
        // Already a full schema with @graph, merge
        return {
          ...customData,
          "@graph": [...defaultData["@graph"], ...(customData["@graph"] || [])]
        }
      }
    }
    return defaultData
  }, [structuredData, getDefaultStructuredData])

  return (
    <Helmet>
      {/* Language and Locale */}
      <html lang={language === 'es' ? 'es-MX' : 'en-US'} />
      <meta property="og:locale" content={language === 'es' ? 'es_MX' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'es' ? 'en_US' : 'es_MX'} />
      
      {/* Primary Meta Tags */}
      <title>{currentContent.title}</title>
      <meta name="title" content={currentContent.title} />
      <meta name="description" content={currentContent.description} />
      <meta name="keywords" content={currentContent.keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1, max-video-duration:300" />
      <meta name="author" content={currentContent.siteName} />
      <meta name="copyright" content={`© ${new Date().getFullYear()} ${currentContent.siteName}`} />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Geographic Targeting */}
      <meta name="geo.region" content="MX-DF" />
      <meta name="geo.placename" content="Mexico City" />
      <meta name="geo.position" content="19.4326;-99.1332" />
      <meta name="ICBM" content="19.4326, -99.1332" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={currentContent.title} />
      <meta property="og:description" content={currentContent.description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${currentContent.siteName} Logo`} />
      <meta property="og:site_name" content={currentContent.siteName} />
      <meta property="og:locale" content={language === 'es' ? 'es_MX' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'es' ? 'en_US' : 'es_MX'} />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={currentContent.title} />
      <meta name="twitter:description" content={currentContent.description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${currentContent.siteName} Logo`} />
      <meta name="twitter:site" content="@logicloud" />
      <meta name="twitter:creator" content="@logicloud" />
      
      {/* LinkedIn */}
      <meta property="og:image" content={image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Language Alternatives with hreflang */}
      <link rel="alternate" hrefLang="en" href={`${fullUrl}?lang=en`} />
      <link rel="alternate" hrefLang="es" href={`${fullUrl}?lang=es`} />
      <link rel="alternate" hrefLang="en-US" href={`${fullUrl}?lang=en`} />
      <link rel="alternate" hrefLang="es-MX" href={`${fullUrl}?lang=es`} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(mergedStructuredData)}
      </script>
      
      {/* Additional SEO Meta Tags */}
      <meta httpEquiv="content-language" content={language === 'es' ? 'es-MX' : 'en-US'} />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={currentContent.siteName} />
      <meta name="application-name" content={currentContent.siteName} />
      <meta name="msapplication-TileColor" content="#1e40af" />
      <meta name="theme-color" content="#1e40af" />
    </Helmet>
  )
}

export default SEO
